/**
 * Lintier carousel scroll indicator.
 *
 * Appends a thin progress bar under every mobile carousel — the
 * homepage best sellers and the two browse rows, and the product
 * gallery (replacing its dots): the ink segment's width is the visible
 * fraction of the track and it slides along as the row scrolls.
 * Mobile-only via CSS; on desktop the arrows remain the affordance.
 */
(function () {
  var CAROUSELS = [
    '[id$="__made_to_order"] slideshow-component',
    '[id$="__browse_by_piece"] slideshow-component',
    '[id$="__browse_by_collection"] slideshow-component',
    'media-gallery slideshow-component',
    '[id$="__recommendations"] slideshow-component',
  ];

  function init() {
    document.querySelectorAll(CAROUSELS.join(', ')).forEach(function (component) {
      var slides = component.querySelector('slideshow-slides');
      if (!slides || component.dataset.lintierProgress) return;
      /* product cards carry their own image slideshow — not a carousel */
      if (component.closest('product-card, .product-card')) return;
      component.dataset.lintierProgress = 'true';

      var bar = document.createElement('div');
      bar.className = 'lintier-carousel-progress';
      bar.setAttribute('aria-hidden', 'true');
      var fill = document.createElement('div');
      fill.className = 'lintier-carousel-progress__fill';
      bar.appendChild(fill);
      component.appendChild(bar);

      function update() {
        var total = slides.scrollWidth;
        var visible = slides.clientWidth;
        if (total <= visible) {
          bar.style.display = 'none';
          return;
        }
        bar.style.removeProperty('display');
        var fraction = visible / total;
        var progress = slides.scrollLeft / (total - visible);
        var trackWidth = bar.clientWidth;
        fill.style.width = fraction * 100 + '%';
        fill.style.transform = 'translateX(' + progress * trackWidth * (1 - fraction) + 'px)';
      }

      slides.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /**
   * Hover wipe on the carousel's header link.
   *
   * Copies the label into data-label so CSS can paint a white duplicate
   * over it and reveal that duplicate with a clip travelling at the same
   * rate as the ink bar. Gated on the class: if this never runs, the link
   * simply has no hover bar rather than grey text on a dark ground.
   */
  function initWipe() {
    document
      .querySelectorAll('[id$="__made_to_order"] .section-resource-list__header a')
      .forEach(function (link) {
        if (link.dataset.label) return;
        var label = link.textContent.trim();
        if (!label) return;
        link.dataset.label = label;
        link.classList.add('lintier-wipe');
      });
  }

  /**
   * Hamburger navigation at every width.
   *
   * Horizon flips the header between an inline menu and a drawer by
   * measuring whether the links fit, writing the result to
   * data-menu-style. Pinning that attribute to 'drawer' reuses the
   * theme's own drawer layout, toggle and focus handling wholesale.
   * The observer wins over the theme's resize re-measurement.
   */
  function forceDrawerNav() {
    var header = document.getElementById('header-component');
    if (!header) return;
    var pin = function () {
      if (header.dataset.menuStyle !== 'drawer') header.dataset.menuStyle = 'drawer';
    };
    pin();
    new MutationObserver(pin).observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
  }

  /**
   * Hero video across the breakpoint.
   *
   * The hero carries two videos (landscape ≥750px, portrait below) and
   * swaps them with CSS. Chrome resumes the newly shown one on its own;
   * Safari can leave a video that was display:none paused on its first
   * frame. On every crossing, play whichever is visible and pause the
   * other, so the swap reads as a swap in every browser.
   */
  function heroVideoSwap() {
    var hero = document.querySelector('[id$="__hero"]');
    if (!hero || !window.matchMedia) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var sync = function () {
      hero.querySelectorAll('video').forEach(function (video) {
        var shown = video.offsetParent !== null && video.getBoundingClientRect().width > 0;
        if (shown) {
          if (video.paused) {
            var p = video.play();
            if (p && p.catch) p.catch(function () {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    };
    var onChange = function () {
      /* let the CSS swap settle before reading visibility */
      setTimeout(sync, 50);
    };
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    window.addEventListener('pageshow', sync);
  }

  function start() {
    init();
    initWipe();
    forceDrawerNav();
    heroVideoSwap();

    /* Product recommendations arrive after load (Horizon fetches them
       through the Section Rendering API), so the carousel is not in the
       DOM yet when init() first runs. Re-run it when that section fills
       in; init() skips components it has already dressed. */
    if (window.MutationObserver) {
      document.querySelectorAll('[id$="__recommendations"]').forEach(function (section) {
        new MutationObserver(init).observe(section, { childList: true, subtree: true });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Shopify's video_tag emits a poster <img> with no alt; mark it decorative. */
(function () {
  function fixPosterAlt() {
    document.querySelectorAll('video img:not([alt]), img[src*="/preview_images/"]:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fixPosterAlt);
  else fixPosterAlt();
  if (window.MutationObserver) new MutationObserver(fixPosterAlt).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Cart note safety net. Horizon's <cart-note> handler is attached by a
   lazily loaded module; a note typed before it lands is lost. This
   listens at the document level from first paint, saves 500ms after
   typing stops and again on blur, and skips when nothing changed. */
(function () {
  var last = null, timer = null, controller = null;
  function save(value) {
    if (value === last) return;
    if (controller) controller.abort();
    controller = new AbortController();
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value }),
      signal: controller.signal,
    }).then(function (r) { if (r.ok) { last = value; syncLines(value); } }).catch(function () {});
  }
  /* Checkout's order summary shows line item properties under each
     product, in small grey text, but not the cart note. Mirror the note
     onto every line as a "Note" property so the customer sees it there
     too. The theme's own cart hides that property (the field is already
     on screen), and admin shows it on each line as well as in Notes. */
  var syncing = false;
  /* A short, single-line preview for checkout: whitespace collapsed,
     cut at a word near 60 characters with an ellipsis. The full note
     stays as the order note in admin. */
  function preview(note) {
    var t = (note || '').replace(/\s+/g, ' ').trim();
    if (t.length <= 60) return t;
    var cut = t.slice(0, 60);
    var sp = cut.lastIndexOf(' ');
    return (sp > 40 ? cut.slice(0, sp) : cut).replace(/[\s,.;:!-]+$/, '') + '\u2026';
  }
  function syncLines(note) {
    if (syncing) return;
    syncing = true;
    var short = preview(note);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart.items.length;
        /* once, on the last line, so it sits just above the discount field */
        var todo = cart.items.map(function (it, i) {
          var want = i === n - 1 ? short : '';
          var cur = (it.properties && it.properties.Note) || '';
          return { it: it, want: want, cur: cur };
        }).filter(function (x) { return x.cur !== x.want; });
        return todo.reduce(function (chain, x) {
          return chain.then(function () {
            var props = Object.assign({}, x.it.properties || {});
            if (x.want) props.Note = x.want; else delete props.Note;
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: x.it.key, quantity: x.it.quantity, properties: props }),
            }).catch(function () {});
          });
        }, Promise.resolve());
      })
      .catch(function () {})
      .then(function () { syncing = false; });
  }
  /* a line added after the note was written gets the note too */
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = origFetch.apply(this, arguments);
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      p.then(function () {
        origFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
          if (c.note) { last = null; syncLines(c.note); }
        }).catch(function () {});
      }).catch(function () {});
    }
    return p;
  };
  function isNote(t) { return t instanceof HTMLTextAreaElement && t.name === 'note'; }
  document.addEventListener('input', function (e) {
    if (!isNote(e.target)) return;
    clearTimeout(timer);
    var v = e.target.value;
    timer = setTimeout(function () { save(v); }, 500);
  });
  document.addEventListener('focusout', function (e) {
    if (!isNote(e.target)) return;
    clearTimeout(timer);
    save(e.target.value);
  });
})();

/* Opening the cart note puts the cursor straight in the field. */
/* Only when the shopper opens it: the note is open by default now, and
   focusing on page load or drawer open would pop the phone keyboard. */
var lintierNoteTapped = 0;
document.addEventListener('click', function (e) {
  if (e.target instanceof Element && e.target.closest('.cart-note__summary')) lintierNoteTapped = Date.now();
}, true);
document.addEventListener('toggle', function (e) {
  var d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open) return;
  if (Date.now() - lintierNoteTapped > 600) return;
  var ta = d.querySelector('textarea[name="note"]');
  if (ta) setTimeout(function () { ta.focus({ preventScroll: true }); }, 120);
}, true);
